import React from 'react';
import { Helmet } from 'react-helmet';

import Link from '../../shared/components/hyperlink';
import JsonLd from '../../shared/components/json-ld';
import CopyToClipboard from '../../shared/components/copy-to-clipboard';
import { withError } from '../../shared/components/error-boundary';
import AdaptiveImage from '../../shared/components/adaptive-image';
import RichContent from '../../utility/components/rich-content';
import RightRail from './right-rail';
import BlogAdContainer from './ads/ad-container';
import ContentCarouselContainer from './content-carousel/content-carousel-container';
import ShareActionButton from '../../social/share-action-button';
import ShareActionBar from '../../social/share-action-bar';
import { Helpers } from '../../core/src/helpers';
import Cta from './cta';
import Disclaimer from './disclaimer';
import SubscribeButton from './right-rail/subscribe-btn';
import { schemaModifiers } from './utils';
import SEOLinks from '../../page/components/seo-links/seo-links';
import AboveTheFoldOnlyServerRender from '../../shared/components/above-the-fold-only-server-render';
import '../css/blog.less';

const DEFAULT_SHARE_BUTTONS = [
    {
        type: 'Facebook',
        utm_source: 'tech_takes',
        utm_content: 'facebook',
    },
    {
        type: 'LinkedIn',
        utm_source: 'tech_takes',
        utm_content: 'linkedin',
    },
    {
        type: 'Twitter',
        utm_source: 'tech_takes',
        utm_content: 'twitter',
    },
    {
        type: 'Reddit',
        utm_source: 'tech_takes',
        utm_content: 'reddit',
    },
];

const initialState = {
    header: 'HP TECH TAKES /...',
    subHeader: "Exploring today's technology for tomorrow's possibilities",
};

const searchFilters = {
    template_key: 'blog',
    hosted: 'true',
    blog_archive: 'false',
};

export const BlogTitleBar = ({ blogDate, authorName, blogTitle }) => {
    let date = Helpers.date.toDateString(new Date(blogDate));

    return (
        <React.Fragment>
            <h1 className="blog-title"> {blogTitle} </h1>
            <div style={{ marginTop: '5px', marginBottom: '20px', textAlign: 'left' }}>
                {authorName.trim().length > 0 && (
                    <div style={{ display: 'inline-block', marginRight: '8px' }}>{authorName}</div>
                )}
                {authorName.trim().length > 0 && <div style={{ display: 'inline-block', marginRight: '8px' }}>|</div>}
                {blogDate && (
                    <div style={{ display: 'inline-block' }} className="article-date">
                        {date}
                    </div>
                )}
            </div>
        </React.Fragment>
    );
};

class Blog extends React.Component {
    processSearchBoxResponse = (response = {}) => {
        return Object.keys(response).length
            ? response.results.map(result => {
                  const { blog_title, vanity_url, blog_thumbnail, blog_date, blog_content, id, description } = result;
                  return {
                      blogTitle: blog_title.raw,
                      vanityUrl: vanity_url.raw,
                      blogThumbnail: JSON.parse(blog_thumbnail.raw),
                      blogDate: blog_date.raw,
                      blogContent: blog_content.raw,
                      description: description.raw,
                      document_id: id.raw,
                  };
              })
            : [];
    };

    getSchemaModifiers = () => {
        const { categories, tags, blogConfig, blogKey, blog, originCountryCode } = this.props;
        const { blogArchive } = blog || {};
        if (!(categories instanceof Array)) return [];
        let contentModifiers = [],
            blockModifiers = [],
            entityModifiers = [],
            dataAggregators = [];
        categories.forEach(cat => {
            if (schemaModifiers[cat]) {
                let { blockModifier, contentModifier, entityModifier, dataAggregator } = schemaModifiers[cat]();
                dataAggregators.unshift(dataAggregator);
                contentModifiers.unshift(contentModifier);
                blockModifiers.unshift(blockModifier);
                entityModifiers.unshift(entityModifier);
            }
        });
        const { hideBlogAd } = blogConfig || {};
        // used for determining where to put the ad banner.
        // hide it for uk blog
        if (!hideBlogAd && !blogArchive) {
            dataAggregators.push(parsed => {
                let paragraphCount = 0;
                try {
                    let blockWithBanners = parsed.blocks.filter((block, index) => {
                        if (
                            (block.type === 'unstyled' || block.type === 'remove-all-styles') &&
                            block.text.length > 0
                        ) {
                            paragraphCount++;
                        }
                        let validParagraph = [2, 10, 18].includes(paragraphCount);

                        if (validParagraph) {
                            paragraphCount++;
                        }

                        return validParagraph;
                    });
                    if (blockWithBanners) {
                        blockWithBanners.forEach((blockWithBanner, idx) => {
                            blockWithBanner.data = blockWithBanner.data || {};
                            blockWithBanner.data.nestedComponent = (
                                <BlogAdContainer
                                    uniqueIdentifier={`blog-content-ad-${idx + 1}`}
                                    bannerType={`banner${idx + 1}`}
                                    bannerMeta={{ categories, tags, bannerKey: blogKey }}
                                    sequence={idx}
                                    type="all"
                                    originCountryCode={originCountryCode}
                                />
                            );
                        });
                    }
                } catch (e) {}
            });
        }

        return { contentModifiers, blockModifiers, entityModifiers, dataAggregators };
    };

    renderCSS = css => <style dangerouslySetInnerHTML={Helpers.createMarkup(css)} />;

    render() {
        const {
            blog = {},
            author = {},
            url = '',
            header,
            subHeader,
            tags = [],
            categories = [],
            _id,
            vanityUrl,
            css,
            wordCloud,
            updatedAt,
            blogKey,
            blogConfig,
            originCountryCode
        } = this.props;
        let { blogTitle = '', blogContent = '', blogThumbnail: image = {}, blogDate = '', hideCta = false } = blog;
        const { authorName = '' } = author;

        if (typeof image === 'string') {
            image = {
                src: image,
                alt: 'HPImage',
                title: 'HPImage',
            };
        } else if (Array.isArray(image)) {
            image = {
                src: image[0],
                alt: 'HPImage',
                title: 'HPImage',
            };
        } else if (!image) {
            image = {
                src: null,
                alt: 'HPImage',
                title: 'HPImage',
            };
        }

        //For JSONLD
        let articleBody = '';
        const blocks = blogContent ? JSON.parse(blogContent).blocks : [];
        for (let block of blocks) {
            articleBody += block.text;
        }

        let dateModified =
            typeof updatedAt === 'string'
                ? updatedAt
                : updatedAt && typeof updatedAt.toISOString === 'function'
                ? updatedAt.toISOString()
                : blogDate;
        let data = {
            headline: blogTitle,
            image: image.src,
            url,
            datePublished: blogDate,
            dateModified,
            articleBody,
            author: {
                '@type': 'Person',
                name: authorName || 'HP',
            },
        };

        const { contentModifiers, blockModifiers, entityModifiers, dataAggregators } =
            this.getSchemaModifiers(vanityUrl) || {};

        const getLink = () => (typeof window !== 'undefined' ? window.location.href : '');
        const { hideSearchBox, hideBlogAd, subscribeCta } = blogConfig || {};
        const { links: subscribeLinks = {}, linkType: subscribeLinkType } = subscribeCta || {};
        const headerClassName = `tech-takes-header${header === initialState.header ? ' hp-blue' : ''}`;
        return (
            <div className="blog-page">
                <Helmet>
                    <meta charSet="utf-8" />
                    <title>{this.props.title}</title>
                    <link rel="canonical" href={this.props.canonical} />
                    <meta name="description" content={this.props.description} />
                    <meta name="keywords" content={this.props.keywords} />

                    <meta property="og:title" content={this.props.title} />
                    <meta property="og:url" content={this.props.canonical} />
                    <meta property="og:description" content={this.props.description} />
                    <meta property="og:image" content={image.src} />

                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:site" value="HP® Tech Takes" />
                    <meta name="twitter:title" content={this.props.title} />
                    <meta name="twitter:description" content={this.props.description} />
                    <meta name="twitter:image" content={image.src} />

                    {this.props.robots && <meta name="robots" content="noindex" />}
                </Helmet>
                <div className="blog-content-container">
                    <div className="hp-tech-takes-header">
                        <div className="hp-tech-takes-title">
                            {header === 'HP Tech@Work' ? (
                                <Link
                                    to={`${process.env.BASENAME}/tech-takes/article-filters/tags=tech-at-work`}
                                    className={headerClassName}
                                >
                                    {header}
                                </Link>
                            ) : (
                                <h2 className={headerClassName}>{header}</h2>
                            )}
                            <div className="tech-takes-subheader">{subHeader}</div>
                        </div>
                        <SubscribeButton
                            metricsElementType={subscribeLinkType}
                            linkId="blog-article-view:subscribe"
                            href={subscribeLinks.BLOG}
                        />
                    </div>
                    <div
                        style={{
                            maxWidth: '1250px',
                            paddingRight: '0px',
                            display: 'inline-block',
                            verticalAlign: 'top',
                            width: '100%',
                        }}
                    >
                        <div className="blog-header-image-container">
                            <Cta tags={tags} hideCta={hideCta} />
                            <AdaptiveImage
                                className="blog-header-image"
                                maxWidth={1000}
                                src={image.src}
                                alt={image.alt}
                                title={image.title}
                            />
                        </div>
                        <div className="wysiwyg-link-container">
                            <Link className="wysiwyg-link home" to={'/tech-takes'}>
                                Home
                            </Link>
                        </div>
                        <ShareActionBar>
                            {!!blogTitle &&
                                DEFAULT_SHARE_BUTTONS.map((share, index) => {
                                    return (
                                        <ShareActionButton
                                            key={index}
                                            type={share.type}
                                            title={blogTitle}
                                            text={share.text}
                                            extraText={share.extraText}
                                            utm_source={share.utm_source}
                                            utm_content={share.utm_content}
                                        />
                                    );
                                })}
                            <ShareActionButton
                                type="Email"
                                title={blogTitle}
                                text="Checkout this article from HP Tech Takes"
                                extraText={
                                    'Just wanted to share this interesting article called "' +
                                    blogTitle +
                                    '". Here\'s the link: ' +
                                    getLink()
                                }
                                utm_source="tech_takes"
                                utm_content="email_link"
                            />
                            <CopyToClipboard title={blogTitle} utm_source="tech_takes" utm_content="copy_link" />
                        </ShareActionBar>
                        <BlogTitleBar blogDate={blogDate} blogTitle={blogTitle} authorName={authorName} />
                        {blogContent && (
                            <RichContent
                                raw={blogContent}
                                overrideNewTab={true}
                                lazyLoadImages={true}
                                imageMaxWidth={1000}
                                entityModifiers={entityModifiers}
                                blockModifiers={blockModifiers}
                                contentModifiers={contentModifiers}
                                dataAggregators={dataAggregators}
                                vanityUrl={vanityUrl}
                                blogKey={blogKey}
                                originCountryCode={originCountryCode}
                            />
                        )}
                        <AboveTheFoldOnlyServerRender skip={true} showTimeout={2000}>
                            <ContentCarouselContainer header={'Recommended articles'} tags={tags} />
                        </AboveTheFoldOnlyServerRender>
                    </div>
                    <JsonLd data={data} type="blog" />
                    {css && this.renderCSS(css)}
                </div>
                <RightRail
                    hideSearchBox={hideSearchBox}
                    hideBlogAd={hideBlogAd}
                    bannerMeta={{ bannerKey: blogKey, categories, tags }}
                    tagsHeader="Related tags"
                    tags={tags}
                    page={'tech-takes-article-view'}
                    subscribeLinkType={subscribeLinkType}
                    subscribeLinkId="blog-article-view:subscribe"
                    subscribeHref={subscribeLinks.BLOG}
                    originCountryCode={originCountryCode}
                />
                <div className="blog-footer-links">
                    <SEOLinks links={wordCloud} useDefaultLinks={true} />
                </div>
                <Disclaimer />
            </div>
        );
    }
}

export default withError(Blog);
